import {
  Box,
  Chip,
  Divider,
  LinearProgress,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useWarRoomSocket } from "../hooks/useWarRoomSocket";

const cityCenters: Record<string, { lat: number; lng: number; zoom?: number }> = {
  Bengaluru: { lat: 12.9716, lng: 77.5946 },
  bangalore: { lat: 12.9716, lng: 77.5946 },
  Mysuru: { lat: 12.2958, lng: 76.6394 },
  Tumakuru: { lat: 13.3409, lng: 77.101 },
  Mandya: { lat: 12.5218, lng: 76.8951 },
  Hassan: { lat: 13.0072, lng: 76.0962 },
  Hyderabad: { lat: 17.385, lng: 78.4867 },
  Chennai: { lat: 13.0827, lng: 80.2707 },
  chennai: { lat: 13.0827, lng: 80.2707 },
  Delhi: { lat: 28.6139, lng: 77.209 },
  Noida: { lat: 28.5355, lng: 77.391 },
  Gurugram: { lat: 28.4595, lng: 77.0266 },
  Mumbai: { lat: 19.076, lng: 72.8777 },
  Pune: { lat: 18.5204, lng: 73.8567 },
  Visakhapatnam: { lat: 17.6868, lng: 83.2185 },
  Kolkata: { lat: 22.5726, lng: 88.3639 },
  Ahmedabad: { lat: 23.0225, lng: 72.5714 },
  Jaipur: { lat: 26.9124, lng: 75.7873 },
  Lucknow: { lat: 26.8467, lng: 80.9462 },
  Surat: { lat: 21.1702, lng: 72.8311 },
  Bhopal: { lat: 23.2599, lng: 77.4126 },
  Indore: { lat: 22.7196, lng: 75.8577 },
  Coimbatore: { lat: 11.0168, lng: 76.9558 },
  Kochi: { lat: 9.9312, lng: 76.2673 },
  Nagpur: { lat: 21.1458, lng: 79.0882 },
  Chandigarh: { lat: 30.7333, lng: 76.7794 },
  Goa: { lat: 15.2993, lng: 74.124 },
};

function getNearestCity(lat: number, lng: number) {
  let minDist = Infinity;
  let nearest = "Unknown";
  Object.entries(cityCenters).forEach(([city, pos]) => {
    const d = Math.sqrt(Math.pow(pos.lat - lat, 2) + Math.pow(pos.lng - lng, 2));
    if (d < minDist) {
      minDist = d;
      nearest = city;
    }
  });
  return nearest;
}

function getStateFromCity(city?: string): string {
  if (!city) return "Unknown";
  const c = city.trim().toLowerCase();
  if (["bengaluru", "bangalore", "mysuru", "mandya", "hassan", "tumakuru"].includes(c)) return "Karnataka";
  if (["chennai", "coimbatore"].includes(c)) return "Tamil Nadu";
  if (["hyderabad"].includes(c)) return "Telangana";
  if (["noida"].includes(c)) return "Uttar Pradesh";
  if (["gurugram"].includes(c)) return "Haryana";
  if (["delhi"].includes(c)) return "Delhi NCR";
  if (["mumbai"].includes(c)) return "Maharashtra";
  if (c.includes("bengaluru") || c.includes("bangalore") || c.includes("mysuru") || c.includes("mandya") || c.includes("hassan") || c.includes("tumakuru")) return "Karnataka";
  if (c.includes("chennai") || c.includes("coimbatore")) return "Tamil Nadu";
  if (c.includes("hyderabad")) return "Telangana";
  if (c.includes("noida")) return "Uttar Pradesh";
  if (c.includes("gurugram")) return "Haryana";
  if (c.includes("delhi")) return "Delhi NCR";
  if (c.includes("mumbai")) return "Maharashtra";
  return "Others";
}

export default function WarRoomSummaryPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const vehicles = useWarRoomSocket();
  const [providerFilter, setProviderFilter] = useState<string | null>("all");

  const handleProviderChange = (_event: React.MouseEvent<HTMLElement>, newProvider: string | null) => {
    setProviderFilter(newProvider || "all");
  };

  const { cityStats, stateStats, overallTotals } = useMemo(() => {
    const cityMap: Record<string, { opspod: number; altmobility: number; total: number }> = {};
    const stateMap: Record<string, number> = {};
    let opspod = 0;
    let altmobility = 0;
    let total = 0;

    vehicles.forEach((v: any) => {
      const id = String(v.vehicleId || v.uniqueId || "");
      if (providerFilter === "opspod" && !id.startsWith("opspod:")) return;
      if (providerFilter === "alt" && !id.startsWith("alt:")) return;

      const city = v.city ? v.city.trim().toLowerCase() : "";
      let matchedCity = Object.keys(cityCenters).find((c) => c.trim().toLowerCase() === city);
      if (!matchedCity || city === "" || city === "unknown") {
        matchedCity = v.lat && v.lng ? getNearestCity(v.lat, v.lng) : "Unknown";
      }

      const normalizedCity = matchedCity ? matchedCity.trim() : "Unknown";
      const state = getStateFromCity(normalizedCity);

      if (!cityMap[normalizedCity]) cityMap[normalizedCity] = { opspod: 0, altmobility: 0, total: 0 };
      if (!stateMap[state]) stateMap[state] = 0;

      if (id.startsWith("opspod:")) {
        cityMap[normalizedCity].opspod++;
        opspod++;
      } else if (id.startsWith("alt:")) {
        cityMap[normalizedCity].altmobility++;
        altmobility++;
      }

      cityMap[normalizedCity].total++;
      stateMap[state]++;
      total++;
    });

    return {
      cityStats: cityMap,
      stateStats: stateMap,
      overallTotals: { opspod, altmobility, total },
    };
  }, [vehicles, providerFilter]);

  const cityRows = Object.entries(cityStats)
    .filter(([city]) => city !== "Unknown")
    .sort((a, b) => b[1].total - a[1].total);
  const stateRows = Object.entries(stateStats).sort((a, b) => b[1] - a[1]);
  const topCity = cityRows[0];
  const maxCityTotal = Math.max(1, ...cityRows.map(([, stats]) => stats.total));
  const maxStateTotal = Math.max(1, ...stateRows.map(([, count]) => count));
  const opsPct = overallTotals.total ? Math.round((overallTotals.opspod / overallTotals.total) * 100) : 0;
  const altPct = overallTotals.total ? Math.round((overallTotals.altmobility / overallTotals.total) * 100) : 0;

  const pageBg = isDark
    ? "radial-gradient(circle at top left, rgba(34,197,94,0.12), transparent 30%), linear-gradient(135deg, #07111f 0%, #0b1324 50%, #071526 100%)"
    : "radial-gradient(circle at top left, rgba(37,99,235,0.14), transparent 32%), linear-gradient(135deg, #eef6ff 0%, #f8fafc 52%, #ecfdf5 100%)";
  const panelSx = {
    borderRadius: 2,
    border: "1px solid",
    borderColor: isDark ? "rgba(56,189,248,0.18)" : "rgba(148,163,184,0.34)",
    background: isDark ? "rgba(15,23,42,0.76)" : "rgba(255,255,255,0.86)",
    color: isDark ? "#e5edf8" : "#0f172a",
    boxShadow: isDark ? "0 24px 58px rgba(0,0,0,0.34)" : "0 22px 52px rgba(15,23,42,0.12)",
    backdropFilter: "blur(18px)",
  };

  return (
    <Box className="mobile-page war-room-summary-page" sx={{ p: { xs: 1.5, md: 3 }, minHeight: "100vh", background: pageBg }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={2} mb={2.5}>
        <Box>
          <Typography variant="h4" fontWeight={950} color={isDark ? "#f8fafc" : "#0f172a"}>
            Vehicle Summary
          </Typography>
          <Typography variant="body2" sx={{ color: isDark ? "#94a3b8" : "#64748b", mt: 0.4, fontWeight: 700 }}>
            Provider, city, and state visibility for the live War Room fleet
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ p: "5px", border: isDark ? "1px solid rgba(148,163,184,0.22)" : "1px solid rgba(226,232,240,0.95)", borderRadius: 2, display: "flex", alignItems: "center", bgcolor: isDark ? "rgba(15,23,42,0.82)" : "rgba(255,255,255,0.9)", width: { xs: "100%", sm: "auto" } }}>
          <Typography variant="caption" sx={{ px: 1.5, color: isDark ? "#94a3b8" : "#64748b", fontWeight: 900, letterSpacing: 0.5 }}>
            FILTER
          </Typography>
          <ToggleButtonGroup value={providerFilter} exclusive onChange={handleProviderChange} aria-label="vehicle provider" size="small" sx={{ "& .MuiToggleButton-root": { border: "none", borderRadius: 2, mx: 0.5, px: 2, py: 0.5, textTransform: "none", fontWeight: 700, color: isDark ? "#cbd5e1" : "#64748b", "&.Mui-selected": { bgcolor: "#2563eb", color: "white", boxShadow: "0 10px 24px rgba(37,99,235,0.26)", "&:hover": { bgcolor: "#1d4ed8" } }, "&:hover": { bgcolor: isDark ? "rgba(148,163,184,0.12)" : "rgba(226,232,240,0.7)" } } }}>
            <ToggleButton value="all">Total</ToggleButton>
            <ToggleButton value="opspod">OpsPod</ToggleButton>
            <ToggleButton value="alt">Alt Mobility</ToggleButton>
          </ToggleButtonGroup>
        </Paper>
      </Stack>

      <Box sx={{ ...panelSx, p: { xs: 2, md: 2.5 }, mb: 2, background: isDark ? "linear-gradient(135deg, rgba(6,30,45,0.92), rgba(7,21,38,0.82))" : "linear-gradient(135deg, rgba(239,246,255,0.94), rgba(236,253,245,0.9))" }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems={{ xs: "stretch", lg: "center" }}>
          <Box sx={{ flex: 1 }}>
            <Chip label="LIVE FLEET SUMMARY" size="small" sx={{ mb: 1, color: "#166534", backgroundColor: "#dcfce7", fontWeight: 950 }} />
            <Typography variant="h2" sx={{ fontWeight: 950, lineHeight: 0.95, fontSize: { xs: 42, md: 64 }, color: isDark ? "#f8fafc" : "#0f172a" }}>
              {overallTotals.total}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, color: isDark ? "#cbd5e1" : "#475569", fontWeight: 800 }}>
              Total vehicles across {cityRows.length} active cities
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
              <Chip label={`Top City: ${topCity ? `${topCity[0]} (${topCity[1].total})` : "NA"}`} sx={{ fontWeight: 900 }} />
              <Chip label={`States: ${stateRows.length}`} sx={{ fontWeight: 900 }} />
            </Stack>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.25, flex: { lg: 1.1 } }}>
            {[
              { label: "OpsPod Vehicles", value: overallTotals.opspod, pct: opsPct, color: "#0ea5e9" },
              { label: "Alt Mobility", value: overallTotals.altmobility, pct: altPct, color: "#22c55e" },
            ].map((item) => (
              <Paper key={item.label} elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid rgba(148,163,184,0.22)", background: isDark ? "rgba(15,23,42,0.72)" : "rgba(255,255,255,0.78)" }}>
                <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 900 }}>{item.label}</Typography>
                <Typography variant="h4" sx={{ color: item.color, fontWeight: 950, lineHeight: 1.1 }}>{item.value}</Typography>
                <LinearProgress variant="determinate" value={item.pct} sx={{ mt: 1.2, height: 8, borderRadius: 8, backgroundColor: isDark ? "rgba(148,163,184,0.16)" : "#e2e8f0", "& .MuiLinearProgress-bar": { backgroundColor: item.color, borderRadius: 8 } }} />
                <Typography variant="caption" sx={{ display: "block", mt: 0.8, fontWeight: 900, color: item.color }}>{item.pct}% fleet mix</Typography>
              </Paper>
            ))}
          </Box>
        </Stack>
      </Box>

      <Box display="grid" gridTemplateColumns={{ xs: "1fr", lg: "2fr 1fr" }} gap={2}>
        <Paper className="city-count-card" sx={{ ...panelSx, p: { xs: 1.5, md: 2 }, overflow: "hidden" }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Typography variant="h6" fontWeight={950}>City-wise Vehicle Count</Typography>
            <Chip size="small" label={`${cityRows.length} cities`} sx={{ fontWeight: 900 }} />
          </Stack>

          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
            <Box component="thead">
              <Box component="tr">
                {["City", "OpsPod", "Alt Mobility", "Total", "Share"].map((head) => (
                  <Box key={head} component="th" sx={{ p: 1.1, textAlign: head === "City" ? "left" : "center", color: isDark ? "#94a3b8" : "#64748b", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {head}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {cityRows.map(([city, stats]) => {
                const percent = Math.round((stats.total / maxCityTotal) * 100);
                return (
                  <Box component="tr" key={city} sx={{ borderTop: isDark ? "1px solid rgba(148,163,184,0.12)" : "1px solid rgba(226,232,240,0.9)", "&:hover": { background: isDark ? "rgba(56,189,248,0.08)" : "rgba(37,99,235,0.05)" } }}>
                    <Box component="td" sx={{ p: 1.1, fontWeight: 900 }}>{city}</Box>
                    <Box component="td" sx={{ p: 1.1, textAlign: "center", color: "#0ea5e9", fontWeight: 900 }}>{stats.opspod}</Box>
                    <Box component="td" sx={{ p: 1.1, textAlign: "center", color: "#22c55e", fontWeight: 900 }}>{stats.altmobility}</Box>
                    <Box component="td" sx={{ p: 1.1, textAlign: "center", fontWeight: 950 }}>{stats.total}</Box>
                    <Box component="td" sx={{ p: 1.1, minWidth: 120 }}>
                      <LinearProgress variant="determinate" value={percent} sx={{ height: 8, borderRadius: 8, backgroundColor: isDark ? "rgba(148,163,184,0.16)" : "#e2e8f0", "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg, #2563eb, #22c55e)", borderRadius: 8 } }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ ...panelSx, p: { xs: 1.5, md: 2 } }}>
          <Typography variant="h6" fontWeight={950} mb={1.5}>State-wise Vehicles</Typography>
          <Stack spacing={1.2}>
            {stateRows.map(([state, count]) => {
              const percent = Math.round((count / maxStateTotal) * 100);
              return (
                <Box key={state} sx={{ p: 1.35, borderRadius: 2, border: isDark ? "1px solid rgba(148,163,184,0.16)" : "1px solid rgba(226,232,240,0.95)", background: isDark ? "rgba(2,6,23,0.26)" : "rgba(248,250,252,0.76)", transition: "all 0.2s ease", "&:hover": { boxShadow: isDark ? "0 10px 24px rgba(0,0,0,0.24)" : "0 10px 24px rgba(15,23,42,0.1)", transform: "translateY(-2px)" } }}>
                  <Box display="flex" justifyContent="space-between" mb={0.7}>
                    <Typography fontWeight={900}>{state}</Typography>
                    <Typography fontWeight={950}>{count}</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={percent} sx={{ height: 8, borderRadius: 8, backgroundColor: isDark ? "rgba(148,163,184,0.16)" : "#e2e8f0", "& .MuiLinearProgress-bar": { borderRadius: 8, background: "linear-gradient(90deg, #6366f1, #22c55e)" } }} />
                </Box>
              );
            })}
          </Stack>
        </Paper>
      </Box>

      <Paper sx={{ ...panelSx, mt: 2, p: { xs: 1.5, md: 2 } }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} spacing={1.5} mb={1.5}>
          <Box>
            <Typography variant="h6" fontWeight={950}>Overall Vehicle Summary</Typography>
            <Typography variant="caption" sx={{ color: isDark ? "#94a3b8" : "#64748b", fontWeight: 800 }}>
              Live mix across selected provider filter
            </Typography>
          </Box>
          <Chip label={`${overallTotals.total} total vehicles`} sx={{ fontWeight: 950, color: "#166534", backgroundColor: "#dcfce7" }} />
        </Stack>
        <Divider sx={{ mb: 1.5, borderColor: isDark ? "rgba(148,163,184,0.14)" : "rgba(226,232,240,0.9)" }} />
        <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(3, 1fr)" }} gap={1.5}>
          {[
            { label: "OpsPod Vehicles", value: overallTotals.opspod, color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" },
            { label: "Alt Mobility Vehicles", value: overallTotals.altmobility, color: "#22c55e", bg: "rgba(34,197,94,0.12)" },
            { label: "Total Vehicles", value: overallTotals.total, color: "#fff", bg: "linear-gradient(135deg, #2563eb, #16a34a)" },
          ].map((item) => (
            <Paper key={item.label} elevation={0} sx={{ p: 2, borderRadius: 2, border: "1px solid rgba(148,163,184,0.2)", background: item.bg, color: item.label === "Total Vehicles" ? "#fff" : "inherit" }}>
              <Typography variant="caption" sx={{ color: item.label === "Total Vehicles" ? "rgba(255,255,255,0.78)" : isDark ? "#94a3b8" : "#64748b", fontWeight: 900 }}>
                {item.label}
              </Typography>
              <Typography variant="h3" sx={{ fontWeight: 950, lineHeight: 1.05, color: item.color }}>
                {item.value}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}
